import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, UploadSimple, X, FileText, CircleNotch } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const MAX_SIZE = 100 * 1024 * 1024;

const fmtSize = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

// Iter209ep — Caricamento opzionale documenti immobile dentro il flusso Marketplace.
export default function PropertyDocsStage({ propertyId, files, onSetFiles, onNext, onBack }) {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);

  const addFiles = (list) => {
    const incoming = Array.from(list || []).filter((f) => {
      if (f.size > MAX_SIZE) {
        toast.error(`${f.name}: file troppo grande (max 100 MB)`);
        return false;
      }
      return true;
    });
    if (incoming.length) onSetFiles([...files, ...incoming]);
  };

  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  };

  const next = async () => {
    if (!files.length) { onNext(); return; }
    if (!propertyId) { toast.error("Seleziona prima un immobile"); return; }
    setUploading(true);
    const failed = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("category", "generic");
      try {
        await api.post(`/properties/${propertyId}/documents`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 10 * 60 * 1000,
        });
      } catch (err) {
        failed.push(f);
        toast.error(`${f.name}: ${err?.response?.data?.detail || "errore upload"}`);
      }
    }
    setUploading(false);
    const ok = files.length - failed.length;
    if (ok) toast.success(ok === 1 ? "Documento caricato" : `${ok} documenti caricati`);
    onSetFiles(failed);
    if (!failed.length) onNext();
  };

  return (
    <div className="space-y-4 py-2" data-testid="stage-property-docs">
      <div>
        <h3 className="font-display text-lg font-semibold">Caricamento documenti</h3>
        <p className="text-sm text-muted-foreground">
          Facoltativo: carica i documenti dell'immobile già in tuo possesso. Puoi anche saltare questo passaggio.
        </p>
      </div>

      <div
        className={`rounded-xl border-2 border-dashed p-6 text-center transition-colors ${dragActive ? "border-purple-500 bg-purple-50/60" : "border-slate-300 bg-slate-50/50"}`}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        data-testid="property-docs-dropzone"
      >
        <UploadSimple size={28} className="mx-auto text-slate-400" />
        <p className="text-sm text-slate-600 mt-2">Trascina qui i file</p>
        <p className="text-xs text-slate-400 mb-3">oppure</p>
        <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="property-docs-pick">
          <Paperclip size={14} className="mr-1.5" /> Scegli file dal PC
        </Button>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
          data-testid="property-docs-input"
        />
      </div>

      {files.length > 0 && (
        <ul className="divide-y rounded-lg border" data-testid="property-docs-list">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`property-docs-item-${i}`}>
              <FileText size={16} className="text-slate-400 shrink-0" />
              <span className="truncate flex-1">{f.name}</span>
              <span className="text-xs text-slate-500 shrink-0">{fmtSize(f.size)}</span>
              <button
                type="button"
                onClick={() => onSetFiles(files.filter((_, j) => j !== i))}
                disabled={uploading}
                className="p-1 rounded hover:bg-slate-100 text-slate-500"
                aria-label={`Rimuovi ${f.name}`}
                data-testid={`property-docs-remove-${i}`}
              >
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <Button className="w-full ht-gradient border-0" onClick={next} disabled={uploading} data-testid="property-docs-next">
        {uploading ? <><CircleNotch size={14} className="mr-2 animate-spin" /> Caricamento…</> : files.length ? "Carica e continua →" : "Salta →"}
      </Button>
      <button type="button" onClick={onBack} disabled={uploading} className="text-sm text-slate-500 hover:text-slate-800" data-testid="property-docs-back">
        ← Indietro
      </button>
    </div>
  );
}
